import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  DEFAULT_OPENING_HOURS,
  PickUpLocationFormData,
  pickUpLocationSchema,
} from "@/types/forms/auth/registerFormData.form";
import { useCreatePickUpLocation } from "@/api/pickUpLocation.api";
import { PickUpLocationForm } from "@/components/pickUpLocation/PickUpLocationForm";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";

type GeneralInfoModalCreatePickupProps = {
  closeModal: () => void;
};

export const GeneralInfoModalCreatePickup = ({ closeModal }: GeneralInfoModalCreatePickupProps) => {
  const { t } = useTranslation(["boClients", "common"]);
  const { mutateAsync: createPickUpLocation, isPending } = useCreatePickUpLocation();

  const pickUpLocationForm = useForm<PickUpLocationFormData>({
    resolver: zodResolver(pickUpLocationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      street: "",
      postalCode: "",
      city: "",
      contactNumber: "",
      contactNumber2: "",
      openingHours: DEFAULT_OPENING_HOURS,
      isDefault: false,
    },
  });

  const onSubmit: SubmitHandler<PickUpLocationFormData> = async (data) => {
    const ret = await createPickUpLocation(data);
    if (ret?.status === 200 || ret?.status === 201) {
      closeModal();
    }
  };

  return (
    <Modal
      open={true}
      onClose={closeModal}
      title={t("profileCreatePickupModal.title")}
      size="md"
      contentClassName="md:min-w-[480px]"
      footer={
        <div className="flex w-full gap-2">
          <Button
            type="submit"
            form="create-pickup-location-form"
            className="flex-1"
            disabled={isPending}
          >
            {t("profileCreatePickupModal.submit")}
          </Button>
          <Button type="button" variant="secondary" onClick={closeModal} className="flex-1">
            {t("common:actions.cancel")}
          </Button>
        </div>
      }
    >
      <form
        id="create-pickup-location-form"
        onSubmit={pickUpLocationForm.handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-3"
      >
        <PickUpLocationForm
          control={pickUpLocationForm.control}
          watch={pickUpLocationForm.watch}
          setValue={pickUpLocationForm.setValue}
          errors={pickUpLocationForm.formState.errors}
          showIsDefault={true}
          register={pickUpLocationForm.register}
        />
      </form>
    </Modal>
  );
};
